import { images } from "@/data/company";

// ============================================================
//  NETFORGE BLOG — edit / add posts here.
//  Each post: unique `slug`, meta, and content sections (h2 + paragraphs).
// ============================================================

export const posts = [
  {
    slug: "custom-crm-vs-spreadsheets",
    title: "Why Your Business Needs a Custom CRM (Not Another Spreadsheet)",
    excerpt:
      "Spreadsheets break the moment your team grows. Here's when a tailored CRM pays for itself — and what to look for.",
    category: "Systems & CRM",
    author: "NetForge Team",
    date: "2026-06-24",
    readTime: "5 min read",
    cover: images.about,
    tags: ["CRM", "Automation", "Business Systems"],
    content: [
      {
        heading: "The hidden cost of spreadsheets",
        body: [
          "Spreadsheets are brilliant for a handful of rows and a single owner. But as soon as three people edit the same file, deals slip through cracks, versions conflict, and nobody trusts the numbers. That friction quietly costs you revenue every week.",
          "A custom CRM replaces that chaos with a single source of truth — every lead, conversation, and follow-up in one place your whole team can rely on.",
        ],
      },
      {
        heading: "When it's time to upgrade",
        body: [
          "If you're copy-pasting between tools, chasing status updates in chat, or losing leads because nobody followed up, you've outgrown spreadsheets. A tailored system automates the busywork so your people focus on customers.",
        ],
      },
      {
        heading: "Build vs. buy",
        body: [
          "Off-the-shelf CRMs are powerful but often bloated and rigid. A custom build maps exactly to how your business actually works — your pipeline stages, your fields, your automations — with none of the noise. At NetForge we design CRMs and internal tools that feel like they were made for your team, because they were.",
        ],
      },
    ],
  },
  {
    slug: "cloud-vs-onprem-hardware-strategy",
    title: "On-Prem vs Cloud: Choosing the Right IT Hardware Strategy",
    excerpt:
      "Not everything belongs in the cloud. A practical framework for deciding what to host, what to own, and how to balance cost and control.",
    category: "IT & Hardware",
    author: "NetForge Team",
    date: "2026-06-10",
    readTime: "6 min read",
    cover: images.heroBg,
    tags: ["Cloud", "IT Hardware", "Infrastructure"],
    content: [
      {
        heading: "There is no one-size-fits-all",
        body: [
          "The 'move everything to the cloud' era is maturing. Smart teams now run hybrid setups — cloud for elasticity and global reach, on-prem or colocated hardware for predictable, high-volume, or latency-sensitive workloads.",
        ],
      },
      {
        heading: "Questions that decide it",
        body: [
          "How predictable is your load? Do you have compliance or data-residency requirements? What's your tolerance for latency? Answering these honestly usually reveals a clear split between what should live in the cloud and what earns its keep on owned hardware.",
        ],
      },
      {
        heading: "Get the setup right the first time",
        body: [
          "Procurement, networking, workstations, servers, and ongoing support are easy to underestimate. NetForge handles hardware end-to-end — from sourcing the right gear to configuring and maintaining it — so your infrastructure is reliable and cost-efficient from day one.",
        ],
      },
    ],
  },
  {
    slug: "seo-foundations-for-startups",
    title: "5 SEO Foundations Every Startup Website Should Nail",
    excerpt:
      "You don't need a huge budget to rank — you need the fundamentals done right. Here are the five that move the needle.",
    category: "Marketing & SEO",
    author: "NetForge Team",
    date: "2026-05-28",
    readTime: "4 min read",
    cover: images.about,
    tags: ["SEO", "Marketing", "Growth"],
    content: [
      {
        heading: "1. A crawlable, fast site",
        body: [
          "Google can't rank what it can't read. Clean URLs, a sitemap, sensible robots rules, and fast load times are table stakes — and they're the cheapest wins available.",
        ],
      },
      {
        heading: "2. Unique titles and descriptions",
        body: [
          "Every important page needs its own title and meta description that match search intent. Generic or duplicate titles quietly cap your visibility.",
        ],
      },
      {
        heading: "3. Content that answers real questions",
        body: [
          "Publish genuinely useful content — like this blog — around the problems your customers search for. Depth and relevance beat keyword stuffing every time.",
        ],
      },
      {
        heading: "4. Structured data",
        body: [
          "Schema markup helps search engines understand your business and can unlock rich results — your logo, ratings, and services shown directly in search.",
        ],
      },
      {
        heading: "5. Real, earned links",
        body: [
          "Backlinks from relevant, reputable sites remain one of the strongest ranking signals. Focus on being genuinely link-worthy rather than gaming it.",
        ],
      },
    ],
  },
  {
    slug: "branding-that-punches-above-its-weight",
    title: "Branding on a Budget: Design That Punches Above Its Weight",
    excerpt:
      "A strong brand isn't about spend — it's about consistency and craft. How small teams can look established from day one.",
    category: "Branding & Design",
    author: "NetForge Team",
    date: "2026-05-12",
    readTime: "4 min read",
    cover: images.heroBg,
    tags: ["Branding", "Graphic Design", "Identity"],
    content: [
      {
        heading: "Consistency is the cheat code",
        body: [
          "A memorable brand comes from using the same colors, type, and voice everywhere — website, socials, decks, invoices. Consistency signals reliability, and reliability wins trust.",
        ],
      },
      {
        heading: "Invest where it's seen most",
        body: [
          "You don't need a full rebrand to look sharp. A clean logo, a tight color system, and well-designed core assets (social templates, a pitch deck, your site) cover 90% of impressions.",
        ],
      },
      {
        heading: "Design is a growth tool",
        body: [
          "Good design reduces friction and increases conversions. NetForge's branding and graphic design work is built to look great and perform — turning attention into action.",
        ],
      },
    ],
  },
  {
    slug: "web-apps-that-scale",
    title: "How We Build Web Apps That Scale",
    excerpt:
      "Speed today, scale tomorrow. The principles behind web apps that stay fast and maintainable as you grow.",
    category: "Development",
    author: "NetForge Team",
    date: "2026-04-30",
    readTime: "5 min read",
    cover: images.about,
    tags: ["Web Development", "Architecture", "Performance"],
    content: [
      {
        heading: "Start simple, design for change",
        body: [
          "Over-engineering kills early momentum; under-engineering creates debt. The sweet spot is a clean, modular architecture that ships fast now and bends — not breaks — as requirements evolve.",
        ],
      },
      {
        heading: "Performance is a feature",
        body: [
          "Users abandon slow apps. We treat load times, responsiveness, and smooth interactions as core requirements, not afterthoughts.",
        ],
      },
      {
        heading: "Own your stack",
        body: [
          "Every NetForge project ships with 100% code ownership and clear documentation — no black boxes, no lock-in. You get a product you can keep building on.",
        ],
      },
    ],
  },
];

export const getPost = (slug) => posts.find((p) => p.slug === slug);
